/* eslint-disable no-plusplus */
/* eslint-disable no-unused-expressions */

import React, {
  FC,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
  useCallback,
} from "react";
import { SerializedStyles } from "@emotion/react";
import { DatePicker, DatePickerProps } from "antd";
import pluralize from "pluralize";
import "antd/lib/date-picker/style/index.less";
import "antd/lib/style/core/motion.less";
import "./overwriteAntdStyles.less";
import * as styles from "./LoftDatePicker.styles";

export type Props = {
  invalid?: boolean;
  css?: SerializedStyles;
  daysDiffIndicator?: boolean;
  maxWidth?: string;
} & DatePickerProps;

const handleKeyUp: EventListener = e => {
  const event = e as KeyboardEvent;
  // returns if the key event doesn't affect the content
  if (event.key === "Shift" || event.key.startsWith("Arrow")) {
    return;
  }
  const inputElement = e.currentTarget as HTMLInputElement;
  // get caret current text selection positions
  let { selectionStart, selectionEnd } = inputElement;
  const { selectionDirection } = inputElement;
  const previousValue = inputElement.value;
  // applies date mask on format mm/dd/aaaa
  const newValue = (inputElement.value || "")
    .toString()
    .replace(/[^0-9.]/g, "") // only numbers
    .slice(0, 8) // limit of characters, we just need 8 numbers to set a date
    .replace(/(\d{2})(\d{1})/, "$1/$2") // add first slash
    .replace(/(\d{2})\/(\d{2})(\d{1})/, "$1/$2/$3"); // add second slash
  if (inputElement.value !== newValue) {
    inputElement.value = newValue;
  }

  // In fact, these two arguments will never be null because if this keyup event
  // is dispatched the caret is inside this same input. But TS doesn't know that.
  if (selectionStart !== null && selectionEnd !== null) {
    // all the logic inside here is to reposition the caret cursor because every
    // time we set a new value to the input element it goes to the end of the
    // content text. So we need to think a little deeper about this behavior.

    // Unfortunately, if the selectionDirection is backward the selectionStart
    // will be after the selectionEnd, for calculation purposes this invert then.
    if (selectionDirection === "backward") {
      const c = selectionStart;
      selectionStart = selectionEnd;
      selectionEnd = c;
    }
    // amount of characters selected
    const selectionRange = selectionEnd - selectionStart;
    // the last event is removing characters from the input value
    if (event.code === "Backspace") {
      // some characters of the input are selected
      if (selectionRange > 0) {
        // the caret stays where it was
        const newPosition = selectionStart;
        inputElement.setSelectionRange(newPosition, newPosition);
      }
      // nothing is selected, but the caret still there
      else {
        let newPosition = selectionStart;
        // if caret the caret if right before a slash
        if ([3, 6].includes(newPosition)) {
          // goes to the position before it, where it has a number
          newPosition--;
        }
        inputElement.setSelectionRange(newPosition, newPosition);
      }
    }
    // the last event is adding a number to the input value
    else if (!Number.isNaN(event.key as any)) {
      selectionStart--;
      const isCaretAtTheEnd = selectionStart === previousValue.length - 1;
      // if the caret was at the end it stills at the end
      if (isCaretAtTheEnd) {
        const newPosition = inputElement.value.length;
        inputElement.setSelectionRange(newPosition, newPosition);
      } else {
        let newPosition = selectionStart + 1;
        if ([2, 5].includes(newPosition)) {
          newPosition++;
        }
        inputElement.setSelectionRange(newPosition, newPosition);
      }
    }
  }
};

type DaysDiffHandles = {
  changeDate: (newDate?: Date) => boolean;
};

type DaysDiffProps = {
  date?: Date;
};

const DaysDiff = forwardRef<DaysDiffHandles, DaysDiffProps>(
  ({ date: date2 }, ref) => {
    const value = useRef<Date | undefined>();
    const [, forceRender] = useState<number>(0);

    useMemo(() => {
      value.current = date2;
    }, [date2]);

    useImperativeHandle(ref, () => ({
      changeDate: newDate => {
        if (newDate !== value.current) {
          value.current = newDate;
          forceRender(new Date().getTime());
          return true;
        }
        return false;
      },
    }));

    if (!value.current) return null;

    // calculates the diff of days between the passed date prop and today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = Math.abs(value.current.getTime() - today.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return (
      <span css={styles.DiffDaysIndicator}>
        {pluralize("Day", diffDays, true)}
      </span>
    );
  }
);
DaysDiff.displayName = "DaysDiff";

type HandleChange = Exclude<DatePickerProps["onChange"], undefined>;

const LoftDatePicker: FC<Props> = ({
  invalid,
  daysDiffIndicator,
  onChange,
  maxWidth,
  ...props
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const daysDiffRef = useRef<DaysDiffHandles>(null);

  useEffect(() => {
    const inputRef = containerRef.current?.getElementsByTagName("input")[0];
    if (inputRef) {
      inputRef.maxLength = 10;
      // event listener to automatically add slashes on typing a date
      inputRef.addEventListener("keyup", handleKeyUp);
    }
    return () => {
      // unmont event removing keyup listener
      inputRef?.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  const handleChange = useCallback<HandleChange>(
    (value, dateString) => {
      if (daysDiffIndicator) {
        const newDate = value ? new Date(dateString) : undefined;
        daysDiffRef.current?.changeDate(newDate);
      }
      onChange?.(value, dateString);
    },
    [daysDiffIndicator, daysDiffRef.current]
  );

  return (
    <div
      ref={containerRef}
      css={[invalid && styles.InvalidStyle, styles.ContainerStyle]}
      style={{ maxWidth }}
    >
      <DatePicker onChange={handleChange} {...props} />
      {daysDiffIndicator && <DaysDiff ref={daysDiffRef} />}
    </div>
  );
};

LoftDatePicker.defaultProps = {
  showToday: false,
  format: "MM/DD/YYYY",
  suffixIcon: <i css={styles.CalendarIcon} className="far fa-calendar" />,
};

export default LoftDatePicker;
